import { Interface } from "../../../pages/interface-definitions";
import { GetIfDefinitionReviewDiffsRequest } from "./GetIfDefinitionReviewDiffsRequest";
import {
  GetIfDefinitionReviewDiffsResponse,
  ReviewDiffCellDetail,
  ReviewDiffChangeType,
  ReviewDiffRowInfo,
  ReviewDiffSideInfo,
} from "./GetIfDefinitionReviewDiffsResponse";

const createSideInfo = (
  changeType: ReviewDiffChangeType,
  diffFieldPaths: string[] = [],
  cellDiffs: Record<string, ReviewDiffCellDetail> = {},
): ReviewDiffSideInfo => ({
  changeType,
  diffFieldPaths,
  cellDiffs,
});

const SRC_DIFF_FIELD_PATHS = [
  "src.interfaceDefinition.inbound.inboundMeta.tableId",
  "src.interfaceDefinition.inbound.inboundMeta.fileId",
  "src.interfaceDefinition.inbound.baseFileId",
  "src.interfaceDefinition.inbound.inboundPartner.permitNoFile",
  "src.interfaceDefinition.inbound.inboundPartner.partnerAddressStg",
  "src.interfaceDefinition.inbound.inboundPartner.partnerAddressPrd",
  "src.interfaceDefinition.inbound.inboundPartner.partnerInitFlg",
  "src.interfaceDefinition.inbound.inboundPartner.remoteFileId",
  "src.interfaceDefinition.inbound.inboundNotification.notificationConfig",
  "src.interfaceDefinition.inbound.inboundNotification.notificationPfId",
  "src.interfaceDefinition.inbound.inboundNotification.notificationType",
  "src.interfaceDefinition.inbound.inboundTransceiver.bqDatasetIdStg",
  "src.interfaceDefinition.inbound.inboundTransceiver.bqDatasetIdPrd",
  "src.interfaceDefinition.inbound.inboundSecurity.contentsAccessibleFlag",
  "src.interfaceDefinition.inbound.inboundSecurity.workFileRetentionDays",
  "src.interfaceDefinition.outbound.sendDatalakeType",
  "src.interfaceDefinition.outbound.fileExtension",
  "src.interfaceDefinition.inbound.talendProject",
  "src.interfaceDefinition.inbound.etlJobOptionsStg",
  "src.interfaceDefinition.inbound.etlJobOptionsPrd",
  "src.interfaceDefinition.inbound.inboundTransceiver.jobName",
  "src.interfaceDefinition.inbound.inboundTransceiver.talendJobVersion",
  "src.interfaceDefinition.inbound.inboundTransceiver.minHeap",
  "src.interfaceDefinition.inbound.inboundTransceiver.maxHeap",
  "src.interfaceDefinition.inbound.inboundTransceiver.concurrentFlag",
  "src.interfaceDefinition.inbound.inboundConverter.jobName",
  "src.interfaceDefinition.inbound.inboundConverter.talendJobVersion",
  "src.interfaceDefinition.inbound.inboundConverter.minHeap",
  "src.interfaceDefinition.inbound.inboundConverter.maxHeap",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.nameValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.postcodeValue",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.addressValue",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.phoneValue",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.emailValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.genderValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.dateOfBirthValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.membershipIdEtcValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.employeeIdValue",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.governmentIssuedIdValue",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.bankAccountInfoValue",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.paymentCardInfoValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.orderInfoValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.purchaseInfoValue",
  "src.interfaceDefinition.inbound.inboundSecurity.dlk.systemInfoValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.deviceInfoValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.geoLocationValue",
  "src.interfaceDefinition.inbound.inboundSecurity.dlk.ipAddressValue",
  "src.interfaceDefinition.inbound.inboundSecurity.slk.sizeInfoValue",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.healthInfoValue",
  "src.interfaceDefinition.inbound.inboundSecurity.piidb.otherValue",
  "src.interfaceDefinition.inbound.inboundSecurity.dataSubjectValue",
  "src.interfaceDefinition.inbound.inboundSecurity.retentionPeriodValue",
  "src.interfaceDefinition.outbound.outboundSecurity.securityInformationTransferFlagValue",
];

const DST_DIFF_FIELD_PATHS = [
  "dst.interfaceDefinition.outbound.outboundMeta.tableId",
  "dst.interfaceDefinition.outbound.outboundMeta.fileId",
  "dst.interfaceDefinition.outbound.outboundNotification.notificationConfig",
  "dst.interfaceDefinition.outbound.outboundNotification.notificationPfId",
  "dst.interfaceDefinition.outbound.outboundNotification.notificationType",
  "dst.interfaceDefinition.outbound.outputFilePrefixStg",
  "dst.interfaceDefinition.outbound.outputFilePrefixPrd",
  "dst.interfaceDefinition.outbound.outputFileBodyStg",
  "dst.interfaceDefinition.outbound.outputFileBodyPrd",
  "dst.interfaceDefinition.outbound.autoPendingFlag",
  "dst.interfaceDefinition.outbound.talendProject",
  "dst.interfaceDefinition.outbound.etlJobOptionsStg",
  "dst.interfaceDefinition.outbound.etlJobOptionsPrd",
  "dst.interfaceDefinition.outbound.outboundConverter.jobName",
  "dst.interfaceDefinition.outbound.outboundConverter.talendJobVersion",
  "dst.interfaceDefinition.outbound.outboundConverter.minHeap",
  "dst.interfaceDefinition.outbound.outboundConverter.maxHeap",
  "dst.interfaceDefinition.outbound.outboundTransceiver.jobName",
  "dst.interfaceDefinition.outbound.outboundTransceiver.talendJobVersion",
  "dst.interfaceDefinition.outbound.outboundTransceiver.minHeap",
  "dst.interfaceDefinition.outbound.outboundTransceiver.maxHeap",
  "dst.interfaceDefinition.outbound.outboundTransceiver.concurrentFlag",
  "dst.interfaceDefinition.outbound.outboundPartner.includeDtd",
  "dst.interfaceDefinition.outbound.outboundPartner.partnerAddressStg",
  "dst.interfaceDefinition.outbound.outboundPartner.partnerAddressPrd",
  "dst.interfaceDefinition.outbound.outboundPartner.remoteFileId",
  "dst.interfaceDefinition.outbound.outboundSecurity.securityInformationTransferFlagValue",
];

const createCellDiffs = (
  paths: readonly string[],
): Record<string, ReviewDiffCellDetail> =>
  paths.reduce<Record<string, ReviewDiffCellDetail>>(
    (cellDiffs, path, index) => {
      cellDiffs[path] = {
        before: `mock_before_${path}`,
        after: `mock_after_${path}`,
      };
      return cellDiffs;
    },
    {},
  );

const createModifiedRowInfo = (): ReviewDiffRowInfo => ({
  src: createSideInfo(
    "modified",
    SRC_DIFF_FIELD_PATHS,
    createCellDiffs(SRC_DIFF_FIELD_PATHS),
  ),
  dst: createSideInfo(
    "modified",
    DST_DIFF_FIELD_PATHS,
    createCellDiffs(DST_DIFF_FIELD_PATHS),
  ),
});

const createAddedRowInfo = (): ReviewDiffRowInfo => ({
  src: createSideInfo("added"),
  dst: createSideInfo("added"),
});

export const createMockIfDefinitionReviewDiffsResponse = (
  request: GetIfDefinitionReviewDiffsRequest,
  currentIfList: Interface[],
): GetIfDefinitionReviewDiffsResponse => {
  const rows = currentIfList.filter((e) => !!e.project?.ifReqId);
  const byIfReqId: Record<string, ReviewDiffRowInfo> = {};

  // 1行目: テンプレートで定義されている全セル差分を表示する modified 行
  if (rows[0]?.project?.ifReqId) {
    byIfReqId[rows[0].project.ifReqId] = createModifiedRowInfo();
  }

  // 2行目: 行追加差分を表示する added 行
  if (rows[1]?.project?.ifReqId) {
    byIfReqId[rows[1].project.ifReqId] = createAddedRowInfo();
  }

  // 3行目: 行削除差分を確認する removed 行
  // if (rows[2]?.project?.ifReqId) {
  //   byIfReqId[rows[2].project.ifReqId] = createRemovedRowInfo();
  // }

  return {
    dfnSetVerNum: request.dfnSetVerNum,
    project: {
      pjtId: request.pjtId,
    },
    compareTargetType: request.compareTargetType,
    byIfReqId,
  };
};
